import { useState } from 'react';
import { Button } from '@marine/ui/button';
import { getErrorCode } from '@marine/utils/getErrorMessage';
import { ComboboxUser, type ComboboxUserItem } from '@/components/combobox/user';
import { useEmployees } from '@/modules/tenant/hooks/useEmployees';
import { useEmployee } from '@/modules/tenant/hooks/useTenantAuth';
import { useAssignLeadFromNotification, assignLeadErrorMessage } from './actions/assignLead';
import { useMarkRead } from './useNotifications';
import type { AppNotification, NotificationAction } from './types';

interface NotificationActionsProps {
  notification: AppNotification;
}

/**
 * #733 — inline actions on a notification row, so a dispatcher assigns an
 * enquiry in one tap without leaving the bell.
 *
 * Kept out of NotificationRow so the row stays presentational: this is the only
 * part that mutates. Every handler stops propagation — the row body is a Link,
 * and tapping a button must never navigate.
 */
export function NotificationActions({ notification }: NotificationActionsProps) {
  const actions = notification.data?.actions ?? [];
  const assignAction = actions.find(
    (action): action is NotificationAction => action.kind === 'assign_lead',
  );

  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [pickerValue, setPickerValue] = useState<string | null>(null);
  const assign = useAssignLeadFromNotification();
  const markRead = useMarkRead();
  const { data: employeesData } = useEmployees({ per_page: 100, is_active: true });
  // The signed-in employee, from the tenant auth store. `tenant` is the app
  // shell, not an optional business module, and Core already imports its
  // tenantApi — so this is not a module-boundary violation.
  const currentEmployee = useEmployee();

  if (!assignAction) return null;

  const employees: ComboboxUserItem[] = (employeesData?.items ?? []).map((employee) => ({
    id: employee.id,
    name: employee.full_name || employee.name,
    email: employee.email,
    avatar: employee.avatar_url ?? undefined,
  }));

  // The display name is passed IN rather than looked up on success: for
  // "Assign to me" the employee list may still be loading, and a lookup miss
  // would show "the rep" to someone who just assigned it to themselves.
  const run = (employeeId: string, displayName: string) => {
    assign.mutate(
      { leadId: assignAction.lead_id, employeeId },
      {
        onSuccess: () => {
          setAssignedTo(displayName);
          if (!notification.is_read) markRead.mutate(notification.id);
        },
        // #889 — the claim-only guard answered: somebody already owns this
        // enquiry, so the card is stale. Retire it — announce the owner, mark
        // the notification read, and withdraw the controls (the render below
        // swaps to a status line); a retry can only conflict again. Other
        // errors (403, network) keep the retryable inline-error path.
        onError: (error) => {
          if (getErrorCode(error) === 'LEAD_ALREADY_ASSIGNED') {
            setConflictMessage(assignLeadErrorMessage(error));
            if (!notification.is_read) markRead.mutate(notification.id);
          }
        },
      },
    );
  };

  if (conflictMessage) {
    // Same announced-status shape as the success branch below (WCAG 2.1 4.1.3).
    return (
      <p role="status" aria-live="polite" className="px-4 pb-3 text-xs font-medium text-muted-foreground">
        {conflictMessage}
      </p>
    );
  }

  if (assignedTo) {
    // #733 fix-round-1 — role="status" + aria-live="polite" so a screen-reader
    // user gets the same "it worked" confirmation a sighted user gets from the
    // text swap (WCAG 2.1 4.1.3 Status Messages).
    return (
      <p role="status" aria-live="polite" className="px-4 pb-3 text-xs font-medium text-muted-foreground">
        Assigned to {assignedTo}
      </p>
    );
  }

  return (
    <div
      className="flex flex-col gap-1 px-4 pb-3"
      onClick={(event) => event.stopPropagation()}
    >
      {assign.isError && (
        // #733 fix-round-1 — a failed assign used to be silent: no onError on
        // the mutation, nothing read here. This mirrors the success
        // confirmation's role="status"/aria-live so a screen-reader user hears
        // the failure too, not just the toast the hook now also raises. The
        // controls below stay enabled (assign.isPending resets to false once
        // the mutation settles) so the dispatcher can just retry.
        <p role="status" aria-live="polite" className="text-xs font-medium text-destructive">
          {assignLeadErrorMessage(assign.error)}
        </p>
      )}
      <div className="flex items-center gap-2">
        <div className="min-w-0 grow" onClick={(event) => event.stopPropagation()}>
          <ComboboxUser
            // #745 — the caption beside this is plain text, not a <Label>,
            // so without this the control has no accessible name at all.
            aria-label="Assign this inquiry to"
            users={employees}
            value={pickerValue}
            onChange={(value) => {
              setPickerValue(value);
              if (!value) return;
              const picked = employees.find((employee) => employee.id === value);
              run(value, picked?.name ?? 'the selected rep');
            }}
            placeholder="Assign to…"
            disabled={assign.isPending}
          />
        </div>
        {currentEmployee && (
          <Button
            size="sm"
            variant="outline"
            disabled={assign.isPending}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              run(currentEmployee.id, 'you');
            }}
          >
            Assign to me
          </Button>
        )}
      </div>
    </div>
  );
}
